import React from 'react';

/* Shared SVG building blocks for the article diagrams. Colors come from
   blog.css (light/dark themed); ids for arrow markers are namespaced per
   diagram so nothing collides. */

export function Figure({ caption, children }) {
    return (
        <figure className="diagram">
            <div className="d-scroll">{children}</div>
            <figcaption>{caption}</figcaption>
        </figure>
    );
}

export function ArrowDefs({ id }) {
    return (
        <defs>
            <marker
                id={id}
                viewBox="0 0 10 10"
                refX="8.5"
                refY="5"
                markerWidth="7"
                markerHeight="7"
                orient="auto-start-reverse"
            >
                <path d="M0,0 L10,5 L0,10 z" className="d-arrowhead" />
            </marker>
        </defs>
    );
}

export function Node({ x, y, w, h, title, sub, sub2, tone = '' }) {
    x = Number(x);
    y = Number(y);
    w = Number(w);
    h = Number(h);
    const cx = x + w / 2;
    const cy = y + h / 2;
    const lines = [title, sub, sub2].filter(Boolean);
    const first = cy - ((lines.length - 1) * 16) / 2 + 5;
    return (
        <g className={`d-node ${tone}`}>
            <rect x={x} y={y} width={w} height={h} rx={10} className="d-box" />
            {lines.map((t, i) => (
                <text
                    key={i}
                    x={cx}
                    y={first + i * 16}
                    textAnchor="middle"
                    className={i === 0 ? 'd-title' : 'd-sub'}
                >
                    {t}
                </text>
            ))}
        </g>
    );
}

export function Pill({ x, y, text }) {
    x = Number(x);
    y = Number(y);
    const w = text.length * 6.3 + 16;
    return (
        <g>
            <rect
                x={x - w / 2}
                y={y - 10.5}
                width={w}
                height={21}
                rx={10.5}
                className="d-pill"
            />
            <text x={x} y={y + 3.5} textAnchor="middle" className="d-pill-text">
                {text}
            </text>
        </g>
    );
}

/* ------------------------------------------------------------------ */
/* 1. The asymmetry: activity bars vs. the knowledge hub              */
/* ------------------------------------------------------------------ */

export function DiagramAsymmetry() {
    const bars = [
        [70, 64, false],
        [120, 98, true],
        [170, 48, false],
        [220, 84, false],
        [270, 36, false],
    ];
    const faint = [
        [430, 236],
        [490, 240],
        [550, 236],
    ];
    return (
        <svg
            viewBox="0 0 720 282"
            role="img"
            aria-label="Left: dashboards show activity as commit bars. Right: a knowledge graph where one person is the sole knower of a module."
        >
            <rect x="8" y="8" width="344" height="246" rx="14" className="d-panel" />
            <rect x="368" y="8" width="344" height="246" rx="14" className="d-panel" />
            <text x="180" y="42" textAnchor="middle" className="d-panel-title">
                what dashboards show
            </text>
            <text x="540" y="42" textAnchor="middle" className="d-panel-title">
                what the graph knows
            </text>

            <line x1="48" y1="206" x2="312" y2="206" className="d-axis" />
            {bars.map(([x, h, hi], i) => (
                <rect
                    key={i}
                    x={x}
                    y={206 - h}
                    width="26"
                    height={h}
                    rx="3"
                    className={hi ? 'd-bar-hi' : 'd-bar'}
                />
            ))}
            <text x="180" y="232" textAnchor="middle" className="d-sub">
                commits · lines · review counts
            </text>

            <line x1="468" y1="150" x2="540" y2="144" className="d-edge-danger" />
            <g className="d-danger">
                <circle cx="450" cy="150" r="17" className="d-dot" />
            </g>
            <text x="450" y="192" textAnchor="middle" className="d-note-danger">
                sole recorded knower
            </text>
            {faint.map(([x, y], i) => (
                <g key={i} className="d-ghost">
                    <circle cx={x} cy={y} r="10" className="d-dot" />
                    <line
                        x1={x}
                        y1={y - 10}
                        x2={i === 2 ? 546 : 554}
                        y2={178}
                        className="d-edge"
                    />
                </g>
            ))}
            <Node x="540" y="122" w="130" h="56" title="Module" sub="client/rest-high-level" />
        </svg>
    );
}

/* ------------------------------------------------------------------ */
/* 2. The graph data model                                            */
/* ------------------------------------------------------------------ */

export function DiagramModel() {
    return (
        <svg
            viewBox="0 0 720 400"
            role="img"
            aria-label="Example of the graph: two people, two modules, a pull request, a doc and a decision, connected by weighted KNOWS edges and evidence edges."
        >
            <line x1="198" y1="95" x2="272" y2="92" className="d-edge-strong" />
            <line x1="112" y1="196" x2="112" y2="126" className="d-edge" />
            <line x1="180" y1="220" x2="280" y2="138" className="d-edge" />
            <line x1="368" y1="136" x2="560" y2="318" className="d-edge" />
            <path d="M 640 126 C 720 160 720 270 648 310" fill="none" className="d-edge" />
            <line x1="448" y1="98" x2="542" y2="198" className="d-edge" />
            <line x1="170" y1="318" x2="310" y2="138" className="d-edge" />

            <Pill x={235} y={78} text="KNOWS" />
            <Pill x={112} y={161} text="AUTHORED" />
            <Pill x={464} y={227} text="co-worked" />
            <Pill x={672} y={282} text="KNOWS" />
            <Pill x={240} y={228} text="AFFECTS" />

            <Node x="30" y="64" w="168" h="62" title="Person" sub="Benjamin Trent" sub2="1,303 commits" />
            <Node x="272" y="48" w="176" h="88" title="Module" sub="client/rest-high-level" tone="accent" />
            <Node x="522" y="64" w="168" h="62" title="Person" sub="Rene Groeschke" />
            <Node x="30" y="196" w="150" h="58" title="Pull Request" sub="review trail" />
            <Node x="540" y="196" w="150" h="58" title="Doc" sub="written trail" />
            <Node x="40" y="318" w="200" h="60" title="Decision" sub="the recorded “why”" />
            <Node x="500" y="318" w="180" h="60" title="Module" sub="rest-api-spec" />
        </svg>
    );
}

/* ------------------------------------------------------------------ */
/* 3. The pipeline: git history to FalkorDB                           */
/* ------------------------------------------------------------------ */

export function DiagramPipeline() {
    const stages = [
        ['git history', 'commits, PRs, docs'],
        ['deterministic ingest', 'rules, not an LLM'],
        ['snapshot', 'sha256-pinned'],
        ['FalkorDB', 'one graph per repo'],
    ];
    const xs = [16, 192, 368, 544];
    return (
        <svg
            viewBox="0 0 720 205"
            role="img"
            aria-label="Pipeline: git history, deterministic ingest, pinned snapshot, then one FalkorDB graph per repository."
        >
            <ArrowDefs id="d3a" />
            {xs.map((x, i) => (
                <g key={i}>
                    <Node x={x} y={54} w={152} h={76} title={stages[i][0]} sub={stages[i][1]} />
                    <circle cx={x + 20} cy={54} r={11} className="d-chip" />
                    <text x={x + 20} y={58.5} textAnchor="middle" className="d-chip-text">
                        {i + 1}
                    </text>
                    {i < 3 && (
                        <line
                            x1={x + 158}
                            y1={92}
                            x2={x + 172}
                            y2={92}
                            className="d-edge"
                            markerEnd="url(#d3a)"
                        />
                    )}
                </g>
            ))}
            <rect x="185" y="158" width="350" height="34" rx="17" className="d-callout" />
            <text x="360" y="179.5" textAnchor="middle" className="d-callout-text">
                same clone, same commit → byte-identical graph
            </text>
        </svg>
    );
}
